// Google's "Sign in with Google" button, wired to Arlo HQ. Renders nothing
// when the server hasn't been given a Google client id, on the wall tablet,
// or in a browser Google won't sign in from (the kiosk's WebView).

import { useEffect, useRef, useState } from 'react';
import { useDevice } from '../../data/hooks.ts';
import { completeGoogleSignIn, googleBlockedHere, renderGoogleButton, useGoogleClientId, type SignedIn } from '../../data/google.ts';
import { toast } from '../../ui/toast.tsx';

export function GoogleSignIn({ onSignedIn, lead }: { onSignedIn?: (r: SignedIn) => void; lead?: string }) {
  const clientId = useGoogleClientId();
  const d = useDevice();
  const box = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Google's script couldn't load (offline, blocked): it's only an alternative, so step aside quietly.
  const [unavailable, setUnavailable] = useState(false);
  const shown = !!clientId && !d.kiosk && !googleBlockedHere() && !unavailable;

  useEffect(() => {
    const el = box.current;
    if (!shown || !el || !clientId) return;
    renderGoogleButton(
      el,
      clientId,
      (credential) => {
        setBusy(true);
        setError('');
        completeGoogleSignIn(credential)
          .then((r) => {
            toast({ message: `Signed in as ${r.name}`, detail: r.email, tone: 'good' });
            onSignedIn?.(r);
          })
          .catch((err: Error) => setError(err.message))
          .finally(() => setBusy(false));
      },
      el.clientWidth || 320,
    ).catch(() => setUnavailable(true));
    // onSignedIn is a fresh closure each render; the latest one is used via the module callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, clientId]);

  if (!shown) return null;
  return (
    <div className="google-signin">
      {lead && <p className="small muted">{lead}</p>}
      <div ref={box} className="google-signin-button" aria-busy={busy} />
      {busy && <p className="small muted">Signing in…</p>}
      {error && <p className="callout tone-bad small">{error}</p>}
    </div>
  );
}
